const JOIN_PREFIX = '/join-workspace/';

/**
 * True when `path` is a join-workspace destination (path or path?query).
 * Used so invite links keep users on "join existing team" instead of create-org onboarding.
 */
export function isJoinWorkspaceCallbackPath(
  path: string | null | undefined
): boolean {
  if (typeof path !== 'string' || path.length === 0) {
    return false;
  }
  const bare = path.split('?')[0] ?? path;
  return bare.startsWith(JOIN_PREFIX);
}

/**
 * Detects invite / join-existing-workspace flows from the current browser URL.
 * When true, global user bootstrap must not redirect to /onboarding.
 */
export function isJoinWorkspaceInviteFlowInBrowser(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const pathname = window.location.pathname;

  if (pathname.startsWith('/join-workspace')) {
    return true;
  }
  if (pathname.startsWith('/invite/')) {
    return true;
  }

  if (pathname === '/signup' || pathname === '/login') {
    const params = new URLSearchParams(window.location.search);
    const cb =
      params.get('callbackURLAfterSignUp') ||
      params.get('callbackURLAfterSignIn');
    return isJoinWorkspaceCallbackPath(cb);
  }

  if (pathname.startsWith('/auth/self-hosted')) {
    const params = new URLSearchParams(window.location.search);
    const cb = params.get('callbackURLAfterSignUp');
    return isJoinWorkspaceCallbackPath(cb);
  }

  return false;
}

/**
 * Missing-DB-user bootstrap: send to /onboarding unless the user is already
 * finishing onboarding or accepting a workspace invite (including /login and
 * /signup with a join-workspace callback query).
 */
export function shouldRedirectMissingUserToOnboarding(
  pathname: string,
  search: string = ''
): boolean {
  if (pathname.startsWith('/onboarding')) {
    return false;
  }
  if (pathname.startsWith('/join-workspace') || pathname.startsWith('/invite/')) {
    return false;
  }
  if (pathname === '/signup' || pathname === '/login') {
    const params = new URLSearchParams(search);
    const cb =
      params.get('callbackURLAfterSignUp') ||
      params.get('callbackURLAfterSignIn');
    if (isJoinWorkspaceCallbackPath(cb)) {
      return false;
    }
  }
  if (pathname.startsWith('/auth/self-hosted')) {
    const params = new URLSearchParams(search);
    if (isJoinWorkspaceCallbackPath(params.get('callbackURLAfterSignUp'))) {
      return false;
    }
  }
  return true;
}
